import type { ChatAssistant } from "@gtm-agents/gtm-ui";
import { createContext, useCallback, useContext, useState } from "react";

interface OpenChatOptions {
  /** Preselect this assistant when the panel opens. */
  assistant?: ChatAssistant;
  /** Pre-populate the input textarea with this prompt. */
  prompt?: string;
}

interface ChatContextValue {
  chatOpen: boolean;
  setChatOpen: (open: boolean | ((prev: boolean) => boolean)) => void;
  /** Open the chat panel with a preselected assistant and/or initial prompt. */
  openChat: (opts?: OpenChatOptions) => void;
  /** Assistant to start the next conversation with. Consumed by AssistantPanel. */
  pendingAssistant: ChatAssistant | undefined;
  /** Text to seed the input on next open. Consumed once by AssistantPanel. */
  pendingInitialInput: string | undefined;
}

const ChatContext = createContext<ChatContextValue>({
  chatOpen: false,
  setChatOpen: () => {},
  openChat: () => {},
  pendingAssistant: undefined,
  pendingInitialInput: undefined,
});

export function ChatProvider({ children }: { children: React.ReactNode }) {
  const [chatOpen, setChatOpen] = useState(false);
  const [pendingAssistant, setPendingAssistant] = useState<ChatAssistant | undefined>();
  const [pendingInitialInput, setPendingInitialInput] = useState<string | undefined>();

  const openChat = useCallback((opts: OpenChatOptions = {}) => {
    setPendingAssistant(opts.assistant);
    setPendingInitialInput(opts.prompt);
    setChatOpen(true);
  }, []);

  return (
    <ChatContext.Provider
      value={{ chatOpen, setChatOpen, openChat, pendingAssistant, pendingInitialInput }}
    >
      {children}
    </ChatContext.Provider>
  );
}

export function useChatContext(): ChatContextValue {
  return useContext(ChatContext);
}
